import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import {
  Loader2,
  AlertTriangle,
  Navigation,
  MapPin,
  Filter,
  Download,
  Compass,
  LayoutGrid,
  Layers,
  X,
} from 'lucide-react';
import apiService from '../services/api';
import { buildNavigationLinks } from '../utils/navigation';
import { buildMapOverridePayload } from '../utils/coordinateQualityUi';
import CommerceMetierBadge from '../components/CommerceMetierBadge';
import useCommerceTaxonomy from '../hooks/useCommerceTaxonomy';
import { resolveMetierLabel } from '../utils/metierDisplay';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet.heat';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { buildMerchantIcon } from '../lib/map/icons';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [46.7111, 1.7191];
const DEFAULT_BOUNDS = [
  [41.0, -5.0],
  [51.5, 10.0],
];
const DEFAULT_ZOOM = 6;
const HEATMAP_MAX_ZOOM = 12;

const COLORS = [
  '#6366F1',
  '#F59E0B',
  '#10B981',
  '#EF4444',
  '#8B5CF6',
  '#14B8A6',
  '#F97316',
  '#EC4899',
  '#0EA5E9',
  '#84CC16',
];

const MapFlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo(target, 13, { duration: 1.1 });
    }
  }, [map, target]);
  return null;
};


const HeatmapLayer = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    let layer;
    if (points && points.length > 0) {
      layer = L.heatLayer(
        points.map((p) => [p.lat, p.lng, p.intensity ?? 1]),
        {
          radius: 25,
          blur: 15,
          maxZoom: HEATMAP_MAX_ZOOM,
        }
      );
      layer.addTo(map);
    }
    return () => {
      if (layer) {
        map.removeLayer(layer);
      }
    };
  }, [map, points]);

  return null;
};

const CarteCommercants = () => {
  const { toasts, toast, removeToast } = useToast();
  const { getMetier: getTaxonomyMetier } = useCommerceTaxonomy();
  const [commercants, setCommercants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    categorie: 'all',
    activeOnly: false, // Afficher tous les commerçants par défaut
    inactiveOnly: false, // Nouveau filtre pour les inactifs
    livraison: false,
    clickAndCollect: false,
    itinerantOnly: false,
  });
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showList, setShowList] = useState(true);
  const mapRef = useRef(null);
  const [editingPosition, setEditingPosition] = useState(null);
  const [newPosition, setNewPosition] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Pour la carte, on récupère TOUS les commerçants (actifs et inactifs)
        // On passe includeInactive=true pour contourner le filtre actif:true du backend
        // On demande une grande pageSize pour récupérer tous les commerçants d'un coup
        const response = await apiService.getCommercants({ 
          includeInactive: true,
          pageSize: 1000 // Récupérer jusqu'à 1000 commerçants (suffisant pour la carte)
        });
        
        // Gérer différentes structures de réponse
        let entries = [];
        if (Array.isArray(response)) {
          entries = response;
        } else if (Array.isArray(response?.data)) {
          entries = response.data;
        } else if (Array.isArray(response?.commercants)) {
          entries = response.commercants;
        } else if (response?.data && Array.isArray(response.data)) {
          entries = response.data;
        }
        
        console.log('[Carte] Commerçants récupérés:', entries.length, entries);

        const normalized = entries.map((item) => ({
          ...item,
          // Utiliser les coordonnées override si disponibles, sinon les coordonnées normales
          latitude:
            item.latitudeOverride !== null && item.latitudeOverride !== undefined
              ? Number(item.latitudeOverride)
              : (item.latitude !== undefined && item.latitude !== null
                  ? Number(item.latitude)
                  : null),
          longitude:
            item.longitudeOverride !== null && item.longitudeOverride !== undefined
              ? Number(item.longitudeOverride)
              : (item.longitude !== undefined && item.longitude !== null
                  ? Number(item.longitude)
                  : null),
          // Conserver les override pour l'affichage
          latitudeOverride: item.latitudeOverride,
          longitudeOverride: item.longitudeOverride,
        }));

        setCommercants(normalized);
        setError('');
      } catch (err) {
        console.error('Erreur chargement commerçants:', err);
        console.error('Détails erreur:', err.message, err.response);
        setError(`Impossible de charger les commerçants: ${err?.message || 'Erreur inconnue'}. Veuillez réessayer plus tard.`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const categories = useMemo(() => {
    const cats = new Set();
    commercants.forEach((c) => {
      if (c.categorie) {
        cats.add(c.categorie);
      }
    });
    return Array.from(cats).sort();
  }, [commercants]);

  const filteredCommercants = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return commercants.filter((c) => {
      // Filtre actif/inactif : si activeOnly est coché, afficher uniquement les actifs
      // Si inactiveOnly est coché, afficher uniquement les inactifs
      if (filters.activeOnly && c.actif !== true) return false;
      if (filters.inactiveOnly && c.actif !== false) return false;
      // Si les deux sont cochés, ne rien afficher (logique ET)
      if (filters.activeOnly && filters.inactiveOnly) return false;
      
      if (filters.itinerantOnly && !c.magasinItinerant) return false;
      if (filters.livraison && !c.livraison) return false;
      if (filters.clickAndCollect && !c.clickAndCollect) return false;
      if (filters.categorie !== 'all' && (c.categorie || '') !== filters.categorie) return false;
      if (query) {
        const target = `${c.nom || ''} ${c.ville || ''} ${c.rue || ''}`.toLowerCase();
        if (!target.includes(query)) return false;
      }
      return true;
    });
  }, [commercants, filters]);

  const selectedCommercant = useMemo(
    () => filteredCommercants.find((c) => c.id === selectedId),
    [filteredCommercants, selectedId]
  );

  useEffect(() => {
    if (selectedId && !filteredCommercants.some((c) => c.id === selectedId)) {
      setSelectedId(null);
    }
  }, [filteredCommercants, selectedId]);

  useEffect(() => {
    if (selectedCommercant && mapRef.current && selectedCommercant.latitude && selectedCommercant.longitude) {
      mapRef.current.flyTo([selectedCommercant.latitude, selectedCommercant.longitude], 13, {
        duration: 1.1,
      });
    }
  }, [selectedCommercant]);

  const stats = useMemo(() => {
    const total = filteredCommercants.length;
    const itin = filteredCommercants.filter((c) => c.magasinItinerant).length;
    const livraison = filteredCommercants.filter((c) => c.livraison).length;
    const clickCollect = filteredCommercants.filter((c) => c.clickAndCollect).length;

    const statutCounts = {};
    commercants.forEach((c) => {
      const statut = c.statut || 'INCONNU';
      statutCounts[statut] = (statutCounts[statut] || 0) + 1;
    });

    const categorieCounts = {};
    filteredCommercants.forEach((c) => {
      const cat = c.categorie || 'Non renseignée';
      categorieCounts[cat] = (categorieCounts[cat] || 0) + 1;
    });

    return {
      total,
      itin,
      livraison,
      clickCollect,
      statutCounts,
      categorieCounts,
    };
  }, [filteredCommercants, commercants]);

  const heatmapPoints = useMemo(
    () =>
      filteredCommercants
        .filter(
          (c) =>
            typeof c.latitude === 'number' &&
            !Number.isNaN(c.latitude) &&
            typeof c.longitude === 'number' &&
            !Number.isNaN(c.longitude)
        )
        .map((c) => ({
          lat: c.latitude,
          lng: c.longitude,
          intensity: 1,
        })),
    [filteredCommercants]
  );

  const statutChartData = useMemo(() => {
    const labels = Object.keys(stats.statutCounts);
    if (labels.length === 0) {
      return null;
    }
    return {
      labels,
      datasets: [
        {
          label: 'Commerçants',
          data: labels.map((label) => stats.statutCounts[label]),
          backgroundColor: labels.map((_, idx) => COLORS[idx % COLORS.length]),
        },
      ],
    };
  }, [stats.statutCounts]);

  const categorieChartData = useMemo(() => {
    const codes = Object.keys(stats.categorieCounts);
    if (codes.length === 0) {
      return null;
    }
    return {
      labels: codes.map((code) => resolveMetierLabel(code, { getMetier: getTaxonomyMetier })),
      datasets: [
        {
          label: 'Catégories',
          data: codes.map((code) => stats.categorieCounts[code]),
          backgroundColor: codes.map((_, idx) => COLORS[idx % COLORS.length]),
        },
      ],
    };
  }, [stats.categorieCounts, getTaxonomyMetier]);

  const handleResetView = () => {
    if (mapRef.current) {
      mapRef.current.fitBounds(DEFAULT_BOUNDS, { padding: [30, 30] });
      setSelectedId(null);
    }
  };

  const handleExportCsv = () => {
    if (!filteredCommercants.length) {
      window.alert('Aucune donnée à exporter.');
      return;
    }
    const headers = [
      'Nom',
      'Catégorie',
      'Statut',
      'Ville',
      'Latitude',
      'Longitude',
      'Itinérant',
      'Livraison',
      'ClickAndCollect',
    ];
    const rows = filteredCommercants.map((c) => [
      c.nom || '',
      c.categorie || '',
      c.statut || '',
      c.ville || '',
      c.latitude ?? '',
      c.longitude ?? '',
      c.magasinItinerant ? 'oui' : 'non',
      c.livraison ? 'oui' : 'non',
      c.clickAndCollect ? 'oui' : 'non',
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const safe = String(cell ?? '');
            if (safe.includes('"') || safe.includes(',') || safe.includes('\n')) {
              return `"${safe.replace(/"/g, '""')}"`;
            }
            return safe;
          })
          .join(',')
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'commercants.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Carte des commerçants</h1>
          <p className="text-sm text-gray-500">
            Visualisez l’ensemble des points de vente, filtrez par statut ou service, et accédez rapidement aux fiches détaillées.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleResetView}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            <Compass className="h-4 w-4" />
            Recentrer
          </button>
          <button
            onClick={() => setShowHeatmap((prev) => !prev)}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              showHeatmap
                ? 'border-blue-200 bg-blue-50 text-blue-700'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Layers className="h-4 w-4" />
            {showHeatmap ? 'Masquer la heatmap' : 'Afficher la heatmap'}
          </button>
          <button
            onClick={() => setShowList((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            <LayoutGrid className="h-4 w-4" />
            {showList ? 'Masquer la liste' : 'Voir la liste'}
          </button>
          <button
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            {loading ? (
              <div className="flex h-[520px] items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              </div>
            ) : (
              <MapContainer
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                scrollWheelZoom
                className="h-[520px] w-full rounded-lg"
                whenCreated={(map) => {
                  mapRef.current = map;
                  map.fitBounds(DEFAULT_BOUNDS, { padding: [40, 40] });
                }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {showHeatmap && heatmapPoints.length > 0 && (
                  <HeatmapLayer points={heatmapPoints} />
                )}
                <MarkerClusterGroup chunkedLoading>
                  {filteredCommercants.map((c) => {
                    if (
                      typeof c.latitude !== 'number' ||
                      Number.isNaN(c.latitude) ||
                      typeof c.longitude !== 'number' ||
                      Number.isNaN(c.longitude)
                    ) {
                      return null;
                    }
                    const navigationLinks = buildNavigationLinks({
                      latitude: c.latitude,
                      longitude: c.longitude,
                      adresseLigne1: c.rue,
                      codePostal: c.codePostal,
                      ville: c.ville,
                      libelleLieu: c.nom,
                    });
                    return (
                      <Marker
                        key={c.id}
                        position={[c.latitude, c.longitude]}
                        icon={buildMerchantIcon(c)}
                        eventHandlers={{
                          click: () => setSelectedId(c.id),
                        }}
                      >
                        <Popup>
                          <div className="space-y-2 text-sm">
                            <p className="font-semibold text-gray-800">{c.nom}</p>
                            <div className="flex flex-wrap gap-1">
                              {c.categorie ? (
                                <CommerceMetierBadge
                                  code={c.categorie}
                                  getMetier={getTaxonomyMetier}
                                  size="sm"
                                />
                              ) : (
                                <span className="text-gray-500 text-xs">Catégorie inconnue</span>
                              )}
                              <span className="text-gray-500 text-xs self-center">
                                • {c.ville || 'Ville inconnue'}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 text-xs">
                              {c.magasinItinerant && (
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-blue-700">
                                  Itinérant
                                </span>
                              )}
                              {c.livraison && (
                                <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-700">
                                  Livraison
                                </span>
                              )}
                              {c.clickAndCollect && (
                                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-purple-700">
                                  Click&Collect
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              {c.rue && (
                                <span className="text-xs text-gray-600">
                                  {c.rue}, {[c.codePostal, c.ville].filter(Boolean).join(' ')}
                                </span>
                              )}
                              {c.latitudeOverride !== null && c.latitudeOverride !== undefined && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                                  Position ajustée manuellement
                                </span>
                              )}
                              {navigationLinks.length > 0 && (
                                <a
                                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                                  href={navigationLinks[0].url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Navigation className="h-3.5 w-3.5" />
                                  Itinéraire
                                </a>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Link
                                to={`/commercants/${c.id}`}
                                className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                <MapPin className="h-3.5 w-3.5" />
                                Voir la fiche
                              </Link>
                              <button
                                onClick={() => {
                                  setEditingPosition(c);
                                  setNewPosition([c.latitude, c.longitude]);
                                }}
                                className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                              >
                                <Navigation className="h-3.5 w-3.5" />
                                Ajuster position
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MarkerClusterGroup>
                {selectedCommercant &&
                  selectedCommercant.latitude &&
                  selectedCommercant.longitude && (
                    <MapFlyTo target={[selectedCommercant.latitude, selectedCommercant.longitude]} />
                  )}
              </MapContainer>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-2 text-sm font-semibold text-gray-700">Statistiques rapides</h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <p className="text-xs text-gray-500">Commerçants filtrés</p>
                  <p className="text-lg font-semibold text-gray-900">{stats.total}</p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-blue-50 px-3 py-2">
                  <p className="text-xs text-blue-600">Itinérants</p>
                  <p className="text-lg font-semibold text-blue-700">{stats.itin}</p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-green-50 px-3 py-2">
                  <p className="text-xs text-green-600">Livraison</p>
                  <p className="text-lg font-semibold text-green-700">{stats.livraison}</p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-purple-50 px-3 py-2">
                  <p className="text-xs text-purple-600">Click & Collect</p>
                  <p className="text-lg font-semibold text-purple-700">{stats.clickCollect}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-2 text-sm font-semibold text-gray-700">Filtres rapides</h2>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.activeOnly}
                    onChange={(e) => {
                      handleFilterChange('activeOnly', e.target.checked);
                      // Si on active "Actifs uniquement", désactiver "Inactifs uniquement"
                      if (e.target.checked && filters.inactiveOnly) {
                        handleFilterChange('inactiveOnly', false);
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Actifs uniquement
                </label>
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.inactiveOnly}
                    onChange={(e) => {
                      handleFilterChange('inactiveOnly', e.target.checked);
                      // Si on active "Inactifs uniquement", désactiver "Actifs uniquement"
                      if (e.target.checked && filters.activeOnly) {
                        handleFilterChange('activeOnly', false);
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-red-600">Inactifs uniquement</span>
                </label>
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.itinerantOnly}
                    onChange={(e) => handleFilterChange('itinerantOnly', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Commerces itinérants
                </label>
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.livraison}
                    onChange={(e) => handleFilterChange('livraison', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Livraison disponible
                </label>
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.clickAndCollect}
                    onChange={(e) => handleFilterChange('clickAndCollect', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Click & Collect
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-4 text-sm font-semibold text-gray-700">Répartition par statut</h2>
            {statutChartData ? (
              <Bar data={statutChartData} options={{ plugins: { legend: { display: false } } }} />
            ) : (
              <p className="text-sm text-gray-500">Aucune donnée pour le moment.</p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Filter className="h-4 w-4" />
              Filtres avancés
            </h2>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-medium text-gray-600">Recherche</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Nom, ville, adresse..."
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Catégorie</label>
                <select
                  value={filters.categorie}
                  onChange={(e) => handleFilterChange('categorie', e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="all">Toutes les catégories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {resolveMetierLabel(cat, { getMetier: getTaxonomyMetier })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {filteredCommercants.length} commerce(s) correspondant(s) aux filtres.
                </p>
                <button
                  onClick={() =>
                    setFilters({
                      search: '',
                      categorie: 'all',
                      activeOnly: false,
                      inactiveOnly: false,
                      livraison: false,
                      clickAndCollect: false,
                      itinerantOnly: false,
                    })
                  }
                  className="text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>

          {categorieChartData && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-3 text-sm font-semibold text-gray-700">Répartition par catégories</h2>
              <Pie data={categorieChartData} />
            </div>
          )}

          {showList && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700">Commerçants affichés</h2>
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                  {filteredCommercants.length}
                </span>
              </div>
              <div className="mt-3 max-h-[320px] space-y-2 overflow-y-auto pr-1">
                {filteredCommercants.length === 0 && (
                  <p className="text-sm text-gray-500">Aucun commerce ne correspond aux filtres.</p>
                )}
                {filteredCommercants.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                      selectedId === c.id
                        ? 'border-blue-300 bg-blue-50 text-blue-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center justify-between text-sm font-medium gap-2">
                      <span className="truncate">{c.nom}</span>
                      {c.categorie ? (
                        <CommerceMetierBadge
                          code={c.categorie}
                          getMetier={getTaxonomyMetier}
                          size="sm"
                        />
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {[c.ville, c.codePostal].filter(Boolean).join(' • ') || 'Localisation inconnue'}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1 text-[10px] uppercase text-gray-400">
                      {c.magasinItinerant && <span>Itinérant</span>}
                      {c.livraison && <span>Livraison</span>}
                      {c.clickAndCollect && <span>Click&Collect</span>}
                      {c.statut && <span>{c.statut}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal pour ajuster la position */}
      {editingPosition && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" onClick={() => {
          setEditingPosition(null);
          setNewPosition(null);
          setMapCenter(null);
        }}>
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto z-[10000]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Correction manuelle — {editingPosition.nom}
                </h2>
                <button
                  onClick={() => {
                    setEditingPosition(null);
                    setNewPosition(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Déplacer le marqueur enregistre une correction manuelle (override) via le panneau
                qualité — la position ne sera pas marquée comme validée ; une vérification restera
                nécessaire. Paire complète obligatoire, hors (0,0).
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={newPosition ? newPosition[0].toFixed(6) : (editingPosition.latitude || '').toFixed(6)}
                    onChange={(e) => {
                      const lat = parseFloat(e.target.value);
                      if (!isNaN(lat)) {
                        setNewPosition([lat, newPosition ? newPosition[1] : (editingPosition.longitude || 0)]);
                      }
                    }}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="46.711100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={newPosition ? newPosition[1].toFixed(6) : (editingPosition.longitude || '').toFixed(6)}
                    onChange={(e) => {
                      const lng = parseFloat(e.target.value);
                      if (!isNaN(lng)) {
                        setNewPosition([newPosition ? newPosition[0] : (editingPosition.latitude || 0), lng]);
                      }
                    }}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1.719100"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse (pour géocodage)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="geocode-address"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`${editingPosition.rue || ''}, ${editingPosition.codePostal || ''} ${editingPosition.ville || ''}`.trim() || "Entrez une adresse complète"}
                  />
                  <button
                    onClick={async () => {
                      const addressInput = document.getElementById('geocode-address');
                      const address = addressInput?.value || '';
                      if (!address.trim()) {
                        toast.warning('Veuillez entrer une adresse');
                        return;
                      }
                      try {
                        // Utiliser l'API de géocodage Nominatim (OpenStreetMap)
                        const response = await fetch(
                          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
                          {
                            headers: {
                              'User-Agent': 'Clickorico-App'
                            }
                          }
                        );
                        const data = await response.json();
                        if (data && data.length > 0) {
                          const lat = parseFloat(data[0].lat);
                          const lon = parseFloat(data[0].lon);
                          setNewPosition([lat, lon]);
                          toast.success('Adresse trouvée ! Coordonnées mises à jour.');
                        } else {
                          toast.error('Adresse non trouvée. Vérifiez l\'adresse et réessayez.');
                        }
                      } catch (err) {
                        console.error('Erreur géocodage:', err);
                        toast.error('Erreur lors du géocodage');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 whitespace-nowrap"
                  >
                    🔍 Rechercher
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Entrez une adresse complète (rue, code postal, ville) et cliquez sur "Rechercher" pour obtenir les coordonnées
                </p>
              </div>
              
              <div className="mb-4 h-64 rounded-lg overflow-hidden border border-gray-300 relative">
                <MapContainer
                  center={newPosition || [editingPosition.latitude, editingPosition.longitude]}
                  zoom={15}
                  scrollWheelZoom={true}
                  className="h-full w-full"
                  key={`map-${newPosition ? `${newPosition[0]}-${newPosition[1]}` : 'default'}`}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {newPosition && (
                    <Marker
                      position={newPosition}
                      draggable={true}
                      eventHandlers={{
                        dragend: (e) => {
                          const { lat, lng } = e.target.getLatLng();
                          setNewPosition([lat, lng]);
                        },
                      }}
                    />
                  )}
                </MapContainer>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setEditingPosition(null);
                    setNewPosition(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    const finalPosition = newPosition || [editingPosition.latitude, editingPosition.longitude];
                    if (!finalPosition || finalPosition.length !== 2) {
                      toast.warning('Veuillez entrer des coordonnées valides');
                      return;
                    }
                    const overridePayload = buildMapOverridePayload(
                      finalPosition[0],
                      finalPosition[1]
                    );
                    if (!overridePayload) {
                      toast.warning(
                        'Paire latitude/longitude complète requise (−90…90 / −180…180, pas (0,0)). Correction manuelle uniquement — non validée.'
                      );
                      return;
                    }
                    try {
                      // G5.2 stratégie A — endpoint override (pas le PUT générique)
                      const qualityRes = await apiService.setCommercantCoordinateOverride(
                        editingPosition.id,
                        overridePayload
                      );
                      const quality = qualityRes?.quality || qualityRes;

                      setCommercants((prev) =>
                        prev.map((c) =>
                          c.id === editingPosition.id
                            ? {
                                ...c,
                                latitudeOverride: overridePayload.latitude,
                                longitudeOverride: overridePayload.longitude,
                                latitude: overridePayload.latitude,
                                longitude: overridePayload.longitude,
                                coordinatesNeedsReview:
                                  quality?.coordinatesNeedsReview ?? true,
                                coordinatesValidatedAt: null,
                              }
                            : c
                        )
                      );

                      setEditingPosition(null);
                      setNewPosition(null);

                      const response = await apiService.getCommercants({
                        includeInactive: true,
                        pageSize: 1000,
                      });
                      let entries = [];
                      if (Array.isArray(response)) {
                        entries = response;
                      } else if (Array.isArray(response?.data)) {
                        entries = response.data;
                      } else if (Array.isArray(response?.commercants)) {
                        entries = response.commercants;
                      }
                      const normalized = entries.map((item) => ({
                        ...item,
                        latitude:
                          item.latitudeOverride !== null && item.latitudeOverride !== undefined
                            ? Number(item.latitudeOverride)
                            : item.latitude !== undefined && item.latitude !== null
                              ? Number(item.latitude)
                              : null,
                        longitude:
                          item.longitudeOverride !== null && item.longitudeOverride !== undefined
                            ? Number(item.longitudeOverride)
                            : item.longitude !== undefined && item.longitude !== null
                              ? Number(item.longitude)
                              : null,
                        latitudeOverride: item.latitudeOverride,
                        longitudeOverride: item.longitudeOverride,
                      }));
                      setCommercants(normalized);

                      toast.success(
                        'Correction manuelle enregistrée (vérification nécessaire — non validée).'
                      );
                    } catch (err) {
                      console.error('Erreur mise à jour position:', err);
                      const msg =
                        err?.error?.message ||
                        err?.message ||
                        'Erreur lors de la mise à jour de la position';
                      toast.error(msg);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  Enregistrer la correction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[10001] space-y-2">
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </div>
  );
};

export default CarteCommercants;

